(ns yardwerkz.web.domain.quote
  "Pure functions for quote display and business logic.
   All functions are testable without React or RFx.

   Quote statuses:
   - :sent      - Quote submitted by worker, awaiting customer action
   - :accepted  - Customer accepted the quote
   - :declined  - Customer rejected the quote
   - :withdrawn - Worker withdrew their quote
   - :expired   - Quote deadline passed")

;; =============================================================================
;; Quote Status Configuration
;; =============================================================================

(def status-configs
  "Display configuration for each quote status."
  {:sent {:label "Pending"
          :color :blue
          :icon "clock-outline"
          :actionable? true}
   :accepted {:label "Accepted"
              :color :green
              :icon "check-circle"
              :actionable? false}
   :declined {:label "Declined"
              :color :red
              :icon "close-circle"
              :actionable? false}
   :withdrawn {:label "Withdrawn"
               :color :gray
               :icon "arrow-u-left-top"
               :actionable? false}
   :expired {:label "Expired"
             :color :amber
             :icon "timer-sand-empty"
             :actionable? false}})

(def default-status-config
  {:label "Unknown"
   :color :gray
   :icon "help-circle"
   :actionable? false})

(defn get-status-config
  "Get display configuration for a quote status."
  [status]
  (get status-configs (keyword status) default-status-config))

(defn status-label
  "Get human-readable label for quote status."
  [status]
  (:label (get-status-config status)))

(defn status-color
  "Get color key for quote status."
  [status]
  (:color (get-status-config status)))

(defn status-icon
  "Get icon name for quote status."
  [status]
  (:icon (get-status-config status)))

;; =============================================================================
;; Quote Status Predicates
;; =============================================================================

(defn sent?
  "Check if quote is in sent/pending status."
  [quote]
  (= :sent (keyword (:status quote))))

(defn accepted?
  "Check if quote has been accepted."
  [quote]
  (= :accepted (keyword (:status quote))))

(defn declined?
  "Check if quote has been declined."
  [quote]
  (= :declined (keyword (:status quote))))

(defn withdrawn?
  "Check if quote has been withdrawn by worker."
  [quote]
  (= :withdrawn (keyword (:status quote))))

(defn expired?
  "Check if quote has expired."
  [quote]
  (= :expired (keyword (:status quote))))

(defn actionable?
  "Check if customer can take action on this quote (accept/decline)."
  [quote]
  (:actionable? (get-status-config (:status quote))))

(defn pending?
  "Check if quote is pending customer action (alias for sent?)."
  [quote]
  (sent? quote))

;; =============================================================================
;; Price Formatting
;; =============================================================================

(defn format-price
  "Format quote amount (in cents) as currency string."
  [amount-cents]
  (when amount-cents
    (str "$" (.toFixed (/ amount-cents 100) 2))))

(defn format-price-short
  "Format quote amount without decimal places for whole dollars."
  [amount-cents]
  (when amount-cents
    (let [dollars (/ amount-cents 100)]
      (if (= dollars (js/Math.floor dollars))
        (str "$" (int dollars))
        (str "$" (.toFixed dollars 2))))))

(defn compare-prices
  "Compare two quote amounts. Returns negative if a < b, positive if a > b."
  [quote-a quote-b]
  (- (or (:amount quote-a) 0) (or (:amount quote-b) 0)))

;; =============================================================================
;; Date Formatting
;; =============================================================================

(def ^:private ms-per-day (* 24 60 60 1000))

(defn- days-ago
  "Calculate days between two dates."
  [date-str]
  (when date-str
    (let [now (js/Date.)
          then (js/Date. date-str)
          diff-ms (- (.getTime now) (.getTime then))]
      (js/Math.floor (/ diff-ms ms-per-day)))))

(defn format-quote-date
  "Format quote created-at date as relative time.
   Returns 'Today', 'Yesterday', or 'N days ago'."
  [date-str]
  (when date-str
    (let [days (days-ago date-str)]
      (cond
        (= days 0) "Today"
        (= days 1) "Yesterday"
        (< days 7) (str days " days ago")
        :else (let [date (js/Date. date-str)
                    month (nth ["Jan" "Feb" "Mar" "Apr" "May" "Jun"
                                "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"]
                               (.getMonth date))
                    day (.getDate date)]
                (str month " " day))))))

(defn format-expiration
  "Format quote expiration time.
   Returns nil if no expiration, or a relative time string."
  [expires-at]
  (when expires-at
    (let [now (js/Date.)
          expiry (js/Date. expires-at)
          diff-ms (- (.getTime expiry) (.getTime now))]
      (cond
        (<= diff-ms 0) "Expired"
        (< diff-ms (* 60 1000)) "Expires in < 1 min"
        (< diff-ms (* 60 60 1000)) (str "Expires in " (js/Math.ceil (/ diff-ms (* 60 1000))) " min")
        (< diff-ms (* 24 60 60 1000)) (str "Expires in " (js/Math.ceil (/ diff-ms (* 60 60 1000))) " hours")
        :else (str "Expires in " (js/Math.ceil (/ diff-ms ms-per-day)) " days")))))

(defn quote-expired?
  "Check if quote has passed its expiration time."
  [quote]
  (when-let [expires-at (:expires-at quote)]
    (let [now (js/Date.)
          expiry (js/Date. expires-at)]
      (<= (.getTime expiry) (.getTime now)))))

;; =============================================================================
;; Worker Display
;; =============================================================================

(defn format-worker-name
  "Format worker name for display, with fallback."
  [quote]
  (or (:worker-name quote) "Worker"))

(defn format-worker-rating
  "Format worker rating for display."
  [quote]
  (when-let [rating (:worker-rating quote)]
    (str (.toFixed rating 1) " rating")))

(defn has-rating?
  "Check if quote has worker rating info."
  [quote]
  (some? (:worker-rating quote)))

;; =============================================================================
;; Filtering & Sorting
;; =============================================================================

(defn filter-actionable
  "Filter to only quotes customer can act on."
  [quotes]
  (filter actionable? quotes))

(defn filter-by-status
  "Filter quotes by status."
  [quotes status]
  (filter #(= (keyword status) (keyword (:status %))) quotes))

(defn sort-by-price-asc
  "Sort quotes by price, lowest first."
  [quotes]
  (sort-by :amount quotes))

(defn sort-by-price-desc
  "Sort quotes by price, highest first."
  [quotes]
  (sort-by :amount #(compare %2 %1) quotes))

(defn sort-by-rating-desc
  "Sort quotes by worker rating, highest first."
  [quotes]
  (sort-by #(or (:worker-rating %) 0) #(compare %2 %1) quotes))

(defn sort-by-date-desc
  "Sort quotes by creation date, newest first."
  [quotes]
  (sort-by :created-at #(compare %2 %1) quotes))

(def sort-options
  "Available sort options for quotes list."
  [{:key :price-asc :label "Price: Low to High" :sort-fn sort-by-price-asc}
   {:key :price-desc :label "Price: High to Low" :sort-fn sort-by-price-desc}
   {:key :rating :label "Best Rated" :sort-fn sort-by-rating-desc}
   {:key :newest :label "Most Recent" :sort-fn sort-by-date-desc}])

(defn apply-sort
  "Apply a sort option to quotes list."
  [quotes sort-key]
  (if-let [option (first (filter #(= sort-key (:key %)) sort-options))]
    ((:sort-fn option) quotes)
    quotes))

;; =============================================================================
;; Quote Summary
;; =============================================================================

(defn quote-count
  "Count total quotes."
  [quotes]
  (count quotes))

(defn pending-count
  "Count quotes pending customer action."
  [quotes]
  (count (filter pending? quotes)))

(defn price-range
  "Get min and max prices from quotes list.
   Returns {:min ... :max ...} in cents, or nil if empty."
  [quotes]
  (when (seq quotes)
    (let [amounts (keep :amount quotes)]
      (when (seq amounts)
        {:min (apply min amounts)
         :max (apply max amounts)}))))

(defn format-price-range
  "Format price range for display."
  [quotes]
  (when-let [{:keys [min max]} (price-range quotes)]
    (if (= min max)
      (format-price min)
      (str (format-price-short min) " - " (format-price-short max)))))

(defn best-rated-quote
  "Get the quote with highest worker rating."
  [quotes]
  (when (seq quotes)
    (first (sort-by-rating-desc (filter-actionable quotes)))))

(defn lowest-price-quote
  "Get the quote with lowest price."
  [quotes]
  (when (seq quotes)
    (first (sort-by-price-asc (filter-actionable quotes)))))

;; =============================================================================
;; Quote Validation
;; =============================================================================

(defn can-accept?
  "Check if a quote can be accepted."
  [quote]
  (and (some? quote)
       (actionable? quote)
       (not (quote-expired? quote))))

(defn can-decline?
  "Check if a quote can be declined."
  [quote]
  (and (some? quote)
       (actionable? quote)))

(defn validate-acceptance
  "Validate quote acceptance.
   Returns {:valid? bool :error string-or-nil}."
  [quote]
  (cond
    (nil? quote)
    {:valid? false :error "No quote selected"}

    (not (actionable? quote))
    {:valid? false :error "This quote can no longer be accepted"}

    (quote-expired? quote)
    {:valid? false :error "This quote has expired"}

    :else
    {:valid? true :error nil}))

;; =============================================================================
;; Quote Card Display Data
;; =============================================================================

(defn build-quote-card-data
  "Build display data for a quote card component."
  [quote]
  {:id (:id quote)
   :worker-name (format-worker-name quote)
   :worker-rating (format-worker-rating quote)
   :has-rating? (has-rating? quote)
   :price-display (format-price (:amount quote))
   :date-display (format-quote-date (:created-at quote))
   :expiration-display (format-expiration (:expires-at quote))
   :status (:status quote)
   :status-label (status-label (:status quote))
   :actionable? (actionable? quote)
   :expired? (quote-expired? quote)})

(defn build-quotes-summary
  "Build summary data for quotes modal header."
  [quotes]
  {:total-count (quote-count quotes)
   :pending-count (pending-count quotes)
   :price-range (format-price-range quotes)
   :has-actionable? (boolean (seq (filter-actionable quotes)))})
